import { getTelegramWebApp, type TelegramWebApp } from "./telegram";

type ThemeParams = NonNullable<TelegramWebApp["themeParams"]>;

function hex(params: ThemeParams, key: string, fallback: string): string {
  const value = params[key];
  return typeof value === "string" && value.length > 0 ? value : fallback;
}

/**
 * Map Telegram.WebApp theme (or OS scheme in the browser) onto document
 * chrome. telegram-web-app.js already writes --tg-theme-* on <html>; this
 * keeps header/background/bottom bar and color-scheme in sync.
 */
export function applyTelegramTheme() {
  const root = document.documentElement;
  const wa = getTelegramWebApp();
  const prefersDark = window.matchMedia?.(
    "(prefers-color-scheme: dark)",
  ).matches;
  const scheme: "light" | "dark" =
    wa?.colorScheme === "light" || wa?.colorScheme === "dark"
      ? wa.colorScheme
      : prefersDark
        ? "dark"
        : "light";

  root.dataset.tgTheme = scheme;
  root.style.colorScheme = scheme;
  document.body?.classList.toggle("dark", scheme === "dark");
  document.documentElement.classList.toggle("dark", scheme === "dark");

  const p = (wa?.themeParams ?? {}) as ThemeParams;
  const secondary = hex(
    p,
    "secondary_bg_color",
    scheme === "dark" ? "#000000" : "#efeff4",
  );
  const header = hex(p, "header_bg_color", secondary);
  const bottom = hex(p, "bottom_bar_bg_color", header);

  try {
    wa?.setHeaderColor?.(header);
    wa?.setBackgroundColor?.(secondary);
    wa?.setBottomBarColor?.(bottom);
  } catch {
    /* older clients */
  }

  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta) themeMeta.setAttribute("content", secondary);
}

export function bindTelegramTheme() {
  applyTelegramTheme();
  const wa = getTelegramWebApp();
  const onTheme = () => applyTelegramTheme();
  wa?.onEvent?.("themeChanged", onTheme);
  const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
  const onScheme = () => {
    if (!getTelegramWebApp()?.themeParams) applyTelegramTheme();
  };
  mq?.addEventListener?.("change", onScheme);
  return () => {
    wa?.offEvent?.("themeChanged", onTheme);
    mq?.removeEventListener?.("change", onScheme);
  };
}
